/**
 * Cache for static reference data (patients, doctors, laboratories)
 * These change infrequently so we cache them for the entire session
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_KEYS = {
  PATIENTS: 'dentec_patients_v4',
  DOCTORS: 'dentec_doctors',
  LABORATORIES: 'dentec_laboratories'
};

const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour for static data

// In-memory cache for instant access
const IN_MEMORY_CACHE: Record<string, CacheEntry<any>> = {};

/**
 * Gets cached data if valid
 */
function getCachedData<T>(key: string): T | null {
  try {
    const now = Date.now();
    
    // Check in-memory first
    if (IN_MEMORY_CACHE[key]) {
      const age = now - IN_MEMORY_CACHE[key].timestamp;
      if (age < CACHE_DURATION_MS) {
        return IN_MEMORY_CACHE[key].data;
      }
    }
    
    // Fall back to localStorage
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    
    const entry: CacheEntry<T> = JSON.parse(cached);
    const age = now - entry.timestamp;
    
    if (age < CACHE_DURATION_MS) {
      // Restore to in-memory cache
      IN_MEMORY_CACHE[key] = entry;
      return entry.data;
    }
    
    // Expired, clear it
    clearCache(key);
    return null;
  } catch (error) {
    console.error(`Error reading cache for ${key}:`, error);
    clearCache(key);
    return null;
  }
}

/**
 * Stores data in both caches
 */
function setCachedData<T>(key: string, data: T): void {
  try {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now()
    };
    
    IN_MEMORY_CACHE[key] = entry;
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (error) {
    console.error(`Error caching data for ${key}:`, error);
  }
}

/**
 * Clears a specific cache
 */
function clearCache(key: string): void {
  try {
    delete IN_MEMORY_CACHE[key];
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error clearing cache for ${key}:`, error);
  }
}

// =====================
// Patient Cache Functions
// =====================

export function getCachedPatients<T>(): T[] | null {
  return getCachedData<T[]>(CACHE_KEYS.PATIENTS);
}

export function setCachedPatients<T>(patients: T[]): void {
  setCachedData(CACHE_KEYS.PATIENTS, patients);
}

export function clearPatientsCache(): void {
  clearCache(CACHE_KEYS.PATIENTS);
}

// =====================
// Doctor Cache Functions
// =====================

export function getCachedDoctors<T>(): T[] | null {
  return getCachedData<T[]>(CACHE_KEYS.DOCTORS);
}

export function setCachedDoctors<T>(doctors: T[]): void {
  setCachedData(CACHE_KEYS.DOCTORS, doctors);
}

export function clearDoctorsCache(): void {
  clearCache(CACHE_KEYS.DOCTORS);
}

// =====================
// Laboratory Cache Functions
// =====================

export function getCachedLaboratories<T>(): T[] | null {
  return getCachedData<T[]>(CACHE_KEYS.LABORATORIES);
}

export function setCachedLaboratories<T>(laboratories: T[]): void {
  setCachedData(CACHE_KEYS.LABORATORIES, laboratories);
}

export function clearLaboratoriesCache(): void {
  clearCache(CACHE_KEYS.LABORATORIES);
}

/**
 * Clears all reference data caches
 */
export function clearAllReferenceDataCaches(): void {
  clearPatientsCache();
  clearDoctorsCache();
  clearLaboratoriesCache();
}
